import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import type { TimelineRunsReportResponse } from "../../types";
import type { AxisMetrics, CursorState, HoverState } from "./layout";
import { clamp01, computeTimelineLayout } from "./layout";

function useAxisMetrics(hasData: boolean) {
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [axisMetrics, setAxisMetrics] = useState<AxisMetrics | null>(null);

  const updateAxisMetrics = useCallback(() => {
    const container = tableContainerRef.current;
    const table = tableRef.current;
    if (!container || !table) return;
    const timelineTh = table.querySelector<HTMLTableCellElement>("th.th-timeline");
    if (!timelineTh) return;
    const containerRect = container.getBoundingClientRect();
    const thRect = timelineTh.getBoundingClientRect();
    setAxisMetrics({
      leftContentPx: container.scrollLeft + (thRect.left - containerRect.left),
      widthPx: Math.max(1, thRect.width),
    });
  }, []);

  useEffect(() => {
    updateAxisMetrics();
    const container = tableContainerRef.current;
    if (!container) return;
    container.addEventListener("scroll", updateAxisMetrics, { passive: true });
    window.addEventListener("resize", updateAxisMetrics);
    return () => {
      container.removeEventListener("scroll", updateAxisMetrics);
      window.removeEventListener("resize", updateAxisMetrics);
    };
  }, [updateAxisMetrics, hasData]);

  return { tableContainerRef, tableRef, axisMetrics };
}

export function useTimelineState(data: TimelineRunsReportResponse | null) {
  const navigate = useNavigate();
  const { tableContainerRef, tableRef, axisMetrics } = useAxisMetrics(data != null);
  const [hover, setHover] = useState<HoverState | null>(null);
  const [cursor, setCursor] = useState<CursorState | null>(null);
  const cursorPendingRef = useRef<CursorState | null>(null);
  const cursorRafRef = useRef<number | null>(null);
  const [rangeDays, setRangeDays] = useState<number>(7);
  const [splitByFunction, setSplitByFunction] = useState<boolean>(false);

  const setCursorThrottled = useCallback((next: CursorState | null) => {
    cursorPendingRef.current = next;
    if (cursorRafRef.current != null) return;
    cursorRafRef.current = requestAnimationFrame(() => {
      cursorRafRef.current = null;
      setCursor(cursorPendingRef.current);
    });
  }, []);

  const layout = useMemo(
    () => computeTimelineLayout(data, rangeDays, splitByFunction),
    [data, rangeDays, splitByFunction],
  );

  const onDotEnter = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, dotKey: string, lines: string[]) => {
      const rect = e.currentTarget.getBoundingClientRect();
      setHover({ left: rect.left + rect.width / 2, top: rect.top, lines, dotKey });
    },
    [],
  );
  const onDotLeave = useCallback(() => setHover(null), []);
  const onBarClick = useCallback(
    (connectorRunId: number) => void navigate(`/detail?connectorRunId=${connectorRunId}`),
    [navigate],
  );

  const onTableMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const container = tableContainerRef.current;
      const table = tableRef.current;
      if (!container || !table) return;
      const containerRect = container.getBoundingClientRect();
      const xContent = container.scrollLeft + (e.clientX - containerRect.left);
      const timelineTh = table.querySelector<HTMLTableCellElement>("th.th-timeline");
      if (!timelineTh) return;
      const thRect = timelineTh.getBoundingClientRect();
      const axisLeftContent = container.scrollLeft + (thRect.left - containerRect.left);
      const axisWidth = Math.max(1, thRect.width);
      const xInAxis = clamp01((xContent - axisLeftContent) / axisWidth);
      setCursorThrottled({
        leftPx: axisLeftContent + xInAxis * axisWidth,
        timeMs: layout.minMs + xInAxis * layout.rangeMs,
      });
    },
    [layout.minMs, layout.rangeMs, setCursorThrottled, tableContainerRef, tableRef],
  );

  const onTableLeave = useCallback(() => setCursorThrottled(null), [setCursorThrottled]);

  const dayGridLines = useMemo(() => {
    const { hasData, minMs, maxMs, rangeMs } = layout;
    if (!hasData || !axisMetrics) return [];
    const lines: Array<{ leftPx: number; key: string; label: string }> = [];
    const d = new Date(minMs);
    d.setHours(0, 0, 0, 0);
    if (d.getTime() <= minMs) d.setDate(d.getDate() + 1);
    while (d.getTime() <= maxMs) {
      const t = d.getTime();
      const x = clamp01((t - minMs) / rangeMs);
      lines.push({
        leftPx: axisMetrics.leftContentPx + x * axisMetrics.widthPx,
        key: String(t),
        label: new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      });
      d.setDate(d.getDate() + 1);
    }
    return lines;
  }, [axisMetrics, layout]);

  return {
    tableContainerRef,
    tableRef,
    hover,
    cursor,
    layout,
    rangeDays: layout.clampedRangeDays,
    setRangeDays,
    splitByFunction,
    setSplitByFunction,
    onDotEnter,
    onDotLeave,
    onBarClick,
    onTableMove,
    onTableLeave,
    dayGridLines,
  };
}
